import 'reflect-metadata';
import { MathUtil } from '../math-util';

const serializedMetadataKey = Symbol('Serialized');

type SerializerFunction<T> = (instance: T) => string;
type DeserializerFunction<T> = (json: string | object) => T;

const handlePrimitive = value => value as any;

interface SerializeMetadata<T> {
  list: boolean;
  serialize: SerializerFunction<T>;
  deserialize: DeserializerFunction<T>;
}

interface SerializedDecoratorConfig<T> {
  list?: true;
  serialize?: SerializerFunction<T>;
  deserialize?: DeserializerFunction<T>;
}

/**
 * Indicate that a value should be serialized, whether a primitive value or a Serializable instance.
 *
 * If the element is an array, you MUST specify `list: true`.
 *
 * @example
 * \@Serialized() public foo: string;
 *
 * @example
 * \@Serialized<SomeType>({
 *   serialize: instance => instance.serialize,
 *   deserialize: SomeType.deserialize,
 * })
 * public foo: SomeType;
 *
 * @example
 * \@Serialized<SomeType>({
 *   list: true,
 *   serialize: instance => instance.serialize,
 *   deserialize: SomeType.deserialize,
 * })
 * public foo: SomeType[];
 *
 * @param config The config defining how a value should be serialized and deserialized. This is mostly boilerplate—see the example usage.
 * @returns Serialization metadata.
 */
export function Serialized<T>(config?: SerializedDecoratorConfig<T>): any {
  const metadata: SerializeMetadata<T> = {
    list: false,
    serialize: handlePrimitive,
    deserialize: handlePrimitive,
  };

  if (config) {
    metadata.list = config.list ?? false;

    if (config.serialize && config.deserialize) {
      metadata.serialize = config.serialize;
      metadata.deserialize = config.deserialize;
    } else if (MathUtil.xor(config.serialize, config.deserialize)) {
      throw Error('Serialized must specify both the serializer and deserializer if either is specified.');
    }
  }


  return Reflect.metadata(serializedMetadataKey, metadata);
}

/**
 * Get the Serialized decorator metadata attached to an object's property.
 *
 * @example
 * class Foo {
 *   @Serialized(...)
 *   public prop: Serializable;
 * }
 * getSerializedMetadata(new Foo(), 'prop');
 *
 * @param target The instance which owns the property you want to look at
 * @param propertyKey The property which may have metadata
 * @returns The metadata object, or undefined if it has no metadata.
 */
export function getSerializedMetadata(target: any, propertyKey: string): SerializeMetadata<any> | undefined {
  return Reflect.getMetadata(serializedMetadataKey, target, propertyKey);
}
